import type { RefObject } from 'react';

/**
 * Run a callback after the sheet panel finishes moving, with a fallback timeout.
 */
const runOnTransitionEndWithTimeout = (
    ref: RefObject<HTMLElement>,
    callback: () => void,
    fallbackTimeout = 1000,
    onTimeoutFinish = () => {},
): (() => void) => {
    const element = ref.current;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    // --- Create cancel and callback functions ---

    const cancelTimeout = () => {
        if (!timeout) return;

        clearTimeout(timeout);
        timeout = null;
    };

    function transitionendCallback(event: TransitionEvent) {
        // The sheet and its children can have other transitions (for example, the panel's border radius),
        // only the panel's own transform transition marks a snap as complete.
        if (event.target !== element || event.propertyName !== 'transform') return;

        element?.removeEventListener('transitionend', transitionendCallback);
        cancelTimeout();
        callback();
    }

    const cancelEventListener = () => {
        element?.removeEventListener('transitionend', transitionendCallback);
    };

    // --- Add listener and timeout ---

    element?.addEventListener('transitionend', transitionendCallback);

    timeout = setTimeout(() => {
        cancelEventListener();
        onTimeoutFinish();
        callback();
    }, fallbackTimeout);

    return () => {
        cancelEventListener();
        cancelTimeout();
    };
};

export default runOnTransitionEndWithTimeout;
